export function presentmentThreshold(thresholdCents, rate = 1) {
  const cents = Number(thresholdCents);
  const factor = Number.isFinite(Number(rate)) && Number(rate) > 0 ? Number(rate) : 1;
  return Number.isFinite(cents) && cents > 0 ? Math.round(cents * factor) : 0;
}

export function freeShippingState(cart, thresholdCents, rate = 1) {
  const threshold = presentmentThreshold(thresholdCents, rate);
  const total = Math.max(0, Number(cart?.total_price) || 0);
  const empty = !cart || !(Number(cart.item_count) > 0);
  const remaining = Math.max(0, threshold - total);
  return {
    threshold,
    total,
    remaining,
    empty,
    reached: threshold > 0 && remaining === 0,
    progress: threshold > 0 ? Math.min(1, total / threshold) : 0,
  };
}

export function freeShippingMessage(state, texts = {}, formatMoney = (cents) => String(cents)) {
  if (state.reached) return String(texts.complete || '');
  return String(texts.progress || '').replace(/\[amount\]/g, formatMoney(state.remaining));
}

export function shortMoney(cents, formatMoney, moneyFormat = '${{amount}}') {
  if (typeof formatMoney !== 'function') return String(cents);
  if (Number(cents) % 100 !== 0) return formatMoney(cents, moneyFormat);
  const whole = String(moneyFormat)
    .replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}')
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, '{{amount_no_decimals_with_comma_separator}}')
    .replace(/\{\{\s*amount_with_space_separator\s*\}\}/, '{{amount_no_decimals_with_space_separator}}');
  return formatMoney(cents, whole);
}

export function rewardSubtotal(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const isGift = (item) => Boolean(item?.properties?._free_gift);
  if (!items.some((item) => !isGift(item))) return 0;
  const gifts = items.filter(isGift).reduce((sum, item) => sum + (Number(item.final_line_price) || 0), 0);
  return Math.max(0, (Number(cart.items_subtotal_price) || 0) - gifts);
}

export function rewardBarState(cart, thresholdsCents, rate = 1) {
  const subtotal = rewardSubtotal(cart);
  const paid = subtotal > 0 || (cart?.items || []).some((item) => !item?.properties?._free_gift);
  const nodes = (thresholdsCents || []).map((cents) => {
    const value = Number(cents);
    return Number.isFinite(value) && value > 0 ? Math.round(value * (Number(rate) > 0 ? Number(rate) : 1)) : 0;
  });
  const reached = nodes.map((threshold) => paid && threshold <= subtotal);
  const pending = nodes.map((threshold, index) => ({ threshold, index })).filter((node) => !reached[node.index]);
  const next = pending.sort((a, b) => a.threshold - b.threshold)[0] || null;
  // Nodes are evenly spaced (centre of equal columns), so the fill is interpolated per segment.
  const sorted = [...nodes].sort((a, b) => a - b);
  let progress = paid && sorted.length ? 1 : 0;
  if (paid) {
    let prevThreshold = 0;
    let prevPos = 0;
    for (let i = 0; i < sorted.length; i += 1) {
      const pos = (i + 0.5) / sorted.length;
      if (subtotal < sorted[i]) {
        const span = sorted[i] - prevThreshold;
        progress = prevPos + (pos - prevPos) * (span > 0 ? (subtotal - prevThreshold) / span : 1);
        break;
      }
      prevThreshold = sorted[i];
      prevPos = pos;
    }
  }
  return {
    subtotal,
    nodes,
    reached,
    next: next ? next.index : -1,
    remaining: next ? Math.max(0, next.threshold - subtotal) : 0,
    complete: paid && !next,
    progress,
  };
}

export function rewardBarMessage(state, labels = [], texts = {}, formatMoney = (cents) => String(cents)) {
  if (state.complete) return String(texts.complete || '');
  if (state.next < 0) return '';
  return String(texts.progress || '')
    .replace(/\[amount\]/g, formatMoney(state.remaining))
    .replace(/\[reward\]/g, String(labels[state.next] || ''));
}

function rate() {
  const value = Number(window.Shopify?.currency?.rate);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

// Returns what the shopper has unlocked ({ ship, reward }, null where the page has no such bar) for SethScales Insights.
function update(cart, scope = document) {
  const seen = { ship: null, reward: null };
  if (!cart) return seen;
  const Theme = window.Theme || {};
  const format = (cents) => shortMoney(cents, Theme.formatMoney, Theme.moneyFormat);
  scope.querySelectorAll('[data-free-shipping]').forEach((root) => {
    const state = freeShippingState(cart, root.dataset.threshold, rate());
    root.hidden = (state.empty && !window.Shopify?.designMode) || !state.threshold;
    root.removeAttribute('data-free-shipping-convert');
    root.classList.toggle('is-complete', state.reached);
    seen.ship = seen.ship || Boolean(state.reached && state.threshold);
    root.querySelector('[data-free-shipping-fill]')?.style.setProperty('--progress', String(state.progress));
    root.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', String(Math.round(state.progress * 100)));
    const message = root.querySelector('[data-free-shipping-message]');
    const text = freeShippingMessage(state, { progress: root.dataset.progressText, complete: root.dataset.completeText }, format);
    if (message && message.textContent.trim() !== text) message.textContent = text;
  });
  if (Theme.abOff?.('milestones')) return seen;
  scope.querySelectorAll('[data-reward-bar]').forEach((root) => {
    const nodes = [...root.querySelectorAll('[data-reward-node]')];
    const state = rewardBarState(cart, nodes.map((node) => node.dataset.threshold), rate());
    seen.reward = seen.reward || state.reached;
    root.hidden = !nodes.length || (!(Number(cart.item_count) > 0) && !window.Shopify?.designMode);
    root.removeAttribute('data-reward-bar-convert');
    root.classList.toggle('is-complete', state.complete);
    root.querySelector('[data-reward-fill]')?.style.setProperty('--progress', String(state.progress));
    root.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', String(Math.round(state.progress * 100)));
    nodes.forEach((node, index) => {
      node.classList.toggle('is-reached', state.reached[index]);
      node.classList.toggle('is-next', index === state.next);
      const amount = node.querySelector('[data-milestone-amount]');
      if (amount) amount.textContent = format(state.nodes[index]);
    });
    const message = root.querySelector('[data-reward-message]');
    const labels = nodes.map((node) => node.dataset.label || '');
    const text = rewardBarMessage(state, labels, { progress: root.dataset.progressText, complete: root.dataset.completeText }, format);
    if (message && message.textContent.trim() !== text) message.textContent = text;
  });
  return seen;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.__freeShippingBound) {
  window.__freeShippingBound = true;
  let latestCart = null;
  // The baseline only moves on cart:updated (renders can fire inside it); renders and boot just seed it.
  const unlocked = {};
  const remember = (seen, changed = false) => {
    if (!window.SSI) return;
    const ss = (name, data) => window.Theme?.ss?.('feat', name, data);
    if (seen.ship !== null && (changed || unlocked.ship === undefined)) {
      if (unlocked.ship === false && seen.ship) ss('free_ship_unlocked', { subtotal: Number(latestCart?.items_subtotal_price) || 0 });
      unlocked.ship = seen.ship;
    }
    if (seen.reward && (changed || !unlocked.reward)) {
      if (unlocked.reward) seen.reward.forEach((hit, index) => hit && !unlocked.reward[index] && ss('milestone_reached', { tier: index + 1, bar: 'reward' }));
      unlocked.reward = seen.reward;
    }
  };

  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.cart) latestCart = event.detail.cart;
    remember(update(latestCart), true);
  });

  document.addEventListener('cart:rendered', (event) => {
    const cart = event.detail?.cart || latestCart;
    if (cart) latestCart = cart;
    remember(update(cart, event.detail?.root || document));
  });

  const boot = () => {
    if (!document.querySelector('[data-free-shipping], [data-reward-bar]')) return;
    const embedded = document.querySelector('[data-cart-json]');
    try {
      latestCart = latestCart || (embedded ? JSON.parse(embedded.textContent) : null);
    } catch {
      latestCart = null;
    }
    if (latestCart) {
      remember(update(latestCart));
      return;
    }
    window.Theme?.cart
      ?.get?.()
      .then((cart) => {
        latestCart = latestCart || cart;
        remember(update(latestCart));
      })
      .catch(() => {});
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
